import React, { useEffect, useState } from "react";
import type { BoxProps } from "@mui/material";
import { Box } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { BaseAtomProps, ComponentSize, ComponentColor } from "../../types";
import { icons } from "./icons";

export type IconName = keyof typeof icons;

export interface IconProps
  extends Omit<BoxProps, "color">,
    BaseAtomProps {
  /**
   * Icon name from icons registry
   */
  name?: IconName;

  /**
   * Icon color
   */
  color?: ComponentColor | "inherit" | "disabled" | string;

  /**
   * Icon size with mobile-first considerations
   */
  size?: ComponentSize | "inherit" | number;

  /**
   * Icon SVG path or component (legacy support)
   */
  icon?: React.ComponentType | string;

  /**
   * Icon name for accessibility (legacy support)
   */
  "aria-label"?: string;
}

const IconComponent: React.FC<IconProps> = ({
  color = "currentColor",
  size = "medium",
  name,
  icon,
  className,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  children,
  sx,
  ...props
}) => {
  const [IconComponent, setIconComponent] =
    useState<React.ComponentType | null>(null);

  // Load icon by name from icons registry
  useEffect(() => {
    if (!name) {
      setIconComponent(null);
      return;
    }

    let mounted = true;

    const loadIcon = async () => {
      try {
        const module = await icons[name]();
        if (mounted) {
          setIconComponent(() => module.default);
        }
      } catch (error) {
        console.error(`Failed to load icon: ${name}`, error);
        if (mounted) {
          setIconComponent(null);
        }
      }
    };

    loadIcon();

    return () => {
      mounted = false;
    };
  }, [name]);

  // Calculate size value
  const sizeValue =
    typeof size === "number"
      ? size
      : size === "small"
        ? { xs: 18, md: 20 }
        : size === "large"
          ? { xs: 28, md: 32 }
          : { xs: 24, md: 24 };

  // Legacy support: if icon prop is provided, use old SvgIcon approach
  if (icon || (!name && !IconComponent)) {
    const iconFontSize: SvgIconProps["fontSize"] =
      size === "small" ? "small" : size === "large" ? "large" : "medium";
    const iconProps: SvgIconProps = {
      color: color as SvgIconProps["color"],
      fontSize: iconFontSize,
      className,
      sx: {
        width: sizeValue,
        height: sizeValue,
        ...(ariaLabel && {
          "aria-label": ariaLabel,
          role: "img",
        }),
        ...sx,
      },
    };

    // If icon is a component, render it
    if (icon && typeof icon !== "string") {
      const LegacyIconComponent = icon;
      return <LegacyIconComponent {...iconProps} data-testid={dataTestId} />;
    }

    // If icon is an SVG path string, render it
    if (icon && typeof icon === "string") {
      return (
        <SvgIcon {...iconProps} data-testid={dataTestId}>
          <path d={icon} />
        </SvgIcon>
      );
    }

    // Otherwise render children (custom SVG content)
    return (
      <SvgIcon {...iconProps} data-testid={dataTestId}>
        {children}
      </SvgIcon>
    );
  }

  // New approach: render icon from registry
  return (
    <Box
      component="span"
      className={className}
      data-testid={dataTestId}
      sx={{
        display: "inline-block",
        width: sizeValue,
        height: sizeValue,
        flexShrink: 0,
        "& svg": {
          display: "block",
          width: "100%",
          height: "100%",
          color: color,
          "& path[stroke]:not([stroke='none'])": {
            stroke: "currentColor",
          },
          "& path[fill]:not([fill='none'])": {
            fill: "currentColor",
            stroke: "none",
          },
          '& circle[fill]:not([fill="none"])': {
            fill: "currentColor",
            stroke: "none",
          },
        },
        ...sx,
      }}
      {...props}
    >
      {IconComponent && <IconComponent />}
    </Box>
  );
};

// Apply memoization with React.memo
export const Icon = React.memo(IconComponent);

// Set display name for debugging
(Icon as any).displayName = "Icon";
